import { useState, useEffect, useCallback } from 'react';
export const useControllableProp = ({ value, defaultValue, }) => {
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState(isControlled ? value : defaultValue);
    const currentValue = isControlled ? value : internalValue;
    const setCurrentValue = isControlled ? undefined : setInternalValue;
    return [currentValue, setCurrentValue];
};
export const useLoadingIndicatorDelay = () => {
    const [isTimeoutExpired, setTimeoutExpired] = useState(false);
    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setTimeoutExpired(true);
        }, 1500);
        return () => {
            clearTimeout(timeout);
        };
    }, []);
    return isTimeoutExpired;
};
export const usePopup = (triggerRef, contentRef, onHide) => {
    const [isShown, setShownState] = useState(false);
    const setShown = (show) => {
        setShownState(show);
        if (show) {
            document.addEventListener('click', handleOutsideClick); // TODO mouseDown
            document.addEventListener('keydown', handleDocumentKeyDown);
            window.addEventListener('resize', handleResize, { once: true });
        }
        else {
            onHide?.();
            document.removeEventListener('click', handleOutsideClick);
            document.removeEventListener('keydown', handleDocumentKeyDown);
            window.removeEventListener('resize', handleResize);
        }
    };
    const handleDocumentKeyDown = useCallback((e) => {
        switch (e.keyCode) {
            case 27: // Escape
                setShown(false);
                break;
        }
    }, []);
    const handleOutsideClick = useCallback((e) => {
        if (triggerRef.current &&
            !triggerRef.current.contains(e.target) &&
            contentRef.current &&
            !contentRef.current.contains(e.target)) {
            setShown(false);
        }
    }, []);
    const handleResize = useCallback(() => {
        setShown(false);
    }, []);
    useEffect(() => {
        return () => {
            document.removeEventListener('click', handleOutsideClick);
            document.removeEventListener('keydown', handleDocumentKeyDown);
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    return [isShown, setShown];
};
export const useDropdown = (popupTriggerRef, popupContentRef, onHide) => {
    const [isShown, setShown] = usePopup(popupTriggerRef, popupContentRef, onHide);
    const handleDropdownKeyDown = useCallback((e) => {
        switch (e.keyCode) {
            case 9: // Tab
                setShown(false); // To not focus elements inside dropdown
                break;
            case 13: // Enter
                e.preventDefault(); // Prevent click
                setShown(!isShown);
                break;
            case 38: // Arrow up
            case 40: // Arrow down
                e.preventDefault(); // Prevent page scrolling
                setShown(true);
                break;
        }
    }, [isShown]);
    return [isShown, setShown, handleDropdownKeyDown];
};
export const useKeyControlledList = (listItemsCount) => {
    const [listItemIndex, setListItemIndex] = useState();
    const addListItemIndex = (number) => {
        listItemIndex === undefined
            ? // Zbytek po celociselnem deleni prirustku stisknuti klavesy nahoru/dolu
                // je indexem aktivni polozky.
                // Aby se predeslo slozitosti s nulou a zapornymi hodnotami,
                // je vychozi hodnota prirustku mocninou celkoveho poctu polozek.
                // Potom tedy pouze staci in/decrementovat pocet stisku sipek nahoru/dolu.
                setListItemIndex(Math.pow(listItemsCount, 10))
            : setListItemIndex(listItemIndex + number);
    };
    const resetListItemIndex = () => {
        setListItemIndex(undefined);
    };
    const handleListControllerKeyDown = useCallback((e) => {
        switch (e.keyCode) {
            case 27: // Escape
                resetListItemIndex();
                break;
            case 38: // Arrow up
                if (listItemsCount) {
                    e.preventDefault();
                    addListItemIndex(-1);
                }
                break;
            case 40: // Arrow down
                if (listItemsCount) {
                    e.preventDefault();
                    addListItemIndex(1);
                }
                break;
        }
    }, [listItemsCount, addListItemIndex, resetListItemIndex]);
    return [
        listItemIndex === undefined ? undefined : listItemIndex % listItemsCount,
        handleListControllerKeyDown,
        resetListItemIndex,
    ];
};
//# sourceMappingURL=hooks.js.map